import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Button from '../../../../../components/Button';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '../../../../../components/Typography';
import AutorenewIcon from '@mui/icons-material/Autorenew';

export default function DeclarationModal({
  open,
  setOpen,
  targetURL,
  onConfirm,
  isLoading,
  /** When set, Cancel / X first show a discard confirmation, then call this (e.g. close Schedule GenPT modal). */
  onDiscardFlow,
  confirmButtonLabel = 'Continue',
}) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setAcceptedTerms(false);
      setCancelConfirmOpen(false);
    }
  }, [open]);

  const closeDeclarationOnly = () => {
    if (isLoading) return;
    setOpen(false);
  };

  const requestCloseDeclaration = () => {
    if (isLoading) return;
    if (onDiscardFlow) {
      setCancelConfirmOpen(true);
    } else {
      closeDeclarationOnly();
    }
  };

  const handleConfirmDiscard = () => {
    setCancelConfirmOpen(false);
    setOpen(false);
    onDiscardFlow?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && isLoading) return;
          requestCloseDeclaration();
        }}
        disableEscapeKeyDown={isLoading}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ m: 0, fontWeight: 600, pb: 1 }}>Asset Declaration</DialogTitle>

        <IconButton
          aria-label="close"
          onClick={requestCloseDeclaration}
          disabled={isLoading}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <Typography className="text-slate-500" gutterBottom>
            I, hereby irrevocably declare and undertake:
          </Typography>

          <List sx={{ listStyle: 'decimal', pl: '16px' }}>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText className="text-slate-500">
                That, I know the Asset that I am testing is{' '}
                <span className="font-bold">{targetURL}</span>
              </ListItemText>
            </ListItem>

            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText className="text-slate-500">
                That, I possess the necessary authorization to conduct vulnerability scanning on the
                designated target. I agree to bear full responsibility for consequences resulting from
                misuse or unauthorized scanning. Siemba disclaims liability for all damages.
              </ListItemText>
            </ListItem>
          </List>

          <div className="flex flex-row items-center mt-3">
            <FormControlLabel
              className="!mr-0"
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={isLoading}
                  sx={{ '&.Mui-checked': { color: '#228EBC' } }}
                />
              }
              label=""
            />
            <p className="text-sm text-gray-600">
              I accept the{' '}
              <button
                type="button"
                className="border-0 bg-transparent p-0 text-primary-500 underline cursor-pointer font-inherit text-sm"
              >
                terms and conditions
              </button>
            </p>
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isLoading || !acceptedTerms}
            className={`${
              isLoading || !acceptedTerms
                ? '!bg-gray-300 cursor-not-allowed !text-gray-500'
                : '!bg-primary-500 hover:!bg-primary-600 !text-white'
            } flex items-center gap-2 !uppercase !tracking-wide !text-xs !font-semibold`}
          >
            {isLoading ? <AutorenewIcon className="animate-spin text-white" /> : confirmButtonLabel}
          </Button>

          <Button
            variant="outlined"
            onClick={requestCloseDeclaration}
            disabled={isLoading}
            className={`${isLoading ? 'cursor-not-allowed opacity-50' : ''} !uppercase !tracking-wide !text-xs !font-semibold !border-primary-500 !text-primary-500`}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 4 }}
        PaperProps={{ sx: { borderRadius: '12px', maxWidth: 'min(520px, calc(100vw - 32px))' } }}
      >
        <DialogContent className="!pt-6">
          <Typography variant="body1" className="!text-gray-700 !leading-relaxed">
            Canceling this declaration will discard all entered details and return you to the asset list.
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            onClick={() => setCancelConfirmOpen(false)}
            className="!bg-primary-500 hover:!bg-primary-600 !text-white !uppercase !tracking-wide !text-xs !font-semibold"
          >
            Go Back
          </Button>
          <Button
            variant="outlined"
            onClick={handleConfirmDiscard}
            className="!uppercase !tracking-wide !text-xs !font-semibold !border-primary-500 !text-primary-500"
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
